/**
 * The Studio drawer's Version History and Compare panels.
 *
 * Redesigned in the Studio pass. Each version used to carry five text-and-icon
 * buttons in a wrapping row — Play, Compare, Load, Rename, Delete — so a song
 * with four versions showed twenty controls of identical weight and nothing said
 * which version was current. Now Version History itself is the draft area, so a
 * version is a row: Play, its label and date, a Compare toggle, and a More menu
 * holding Load, Rename, Duplicate, Save to library and Delete. The most recent one
 * is marked "Latest".
 *
 * "Latest" rather than "Current": the song record does not track which version is
 * live, so the honest claim is the one that can be derived. See src/lib/versions.ts.
 *
 * Accessible names stay explicit now that Load is the only version-selection verb:
 * one version-selection verb, and Compare uses “Load this” rather than restoring.
 *
 * SIMULATED. There is no audio; "playback" is a clock — see playerStore. Each row's Play
 * button cues the *version* (playerStore.toggleVersion), so the version's own runtime drives
 * the transport's clock, total time and scrubber, and the button cycles Play → spinner →
 * Pause. "Load" (in the More menu) cues the version paused AND retargets the edit tools onto
 * it. The prototype instead passed a composite `${songId}-${versionId}` that matched no song
 * and left the player stuck; per-version *audio* still awaits real playback, but the runtime
 * shown is the version's, which is what the clock counts.
 */
import { useEffect, useRef, useState } from 'react';
import { Copy, FolderInput, GitCompare, Library, Loader2, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { ActionMenu, Btn, IconButton } from '../ui';
import { Waveform } from '../player';
import DrawerHeader from './DrawerHeader';
import { useLiveDateLabel } from '../../hooks/useLiveDateLabel';
import { seededHeights } from '../../lib/waveform';
import { latestVersion } from '../../lib/versions';
import { c } from '../../styles/theme';
import type { Song, SongVersion } from '../../types/domain';

/**
 * Shared panel padding, identical in both panels.
 *
 * A class rather than an inline padding so it can narrow on a phone — an inline
 * style outranks a media query. Same rule as the tool panels; see .lyx-tool-panel.
 */
const PANEL_CLASS = 'lyx-tool-panel';

/**
 * How long the simulated load window lasts before a cold-started version begins playing.
 *
 * Seed songs carry no audio (see the file header and playerStore), so there is nothing to
 * buffer and no real loading signal to reflect. This brief window is the same simulated
 * affordance the Studio's own actions use — useStudio's 1300ms timers — scaled down because
 * cueing a known version is meant to feel quicker than generating one.
 */
const LOAD_WINDOW_MS = 500;

/**
 * A version row's Play control, cycling Play → spinner → Pause as the Studio pass asked.
 *
 * The three states come from two places, deliberately. Playing-vs-paused is read from the
 * player store through `isActive`/`isPlaying`, so the button and the transport can never
 * disagree about what is playing. The spinner is LOCAL and transient: a cold start (a
 * version that is not the loaded one) shows it for one `LOAD_WINDOW_MS` and then begins
 * playback, which is the simulated stand-in for buffering. Pressing the button on the
 * version already loaded is an instant pause/resume with no window — the position is held in
 * the store and there is nothing to load — so the spinner is only ever the first-play flash.
 */
function VersionPlayButton({
  label,
  iconSize,
  isActive,
  isPlaying,
  onToggle,
}: {
  /** The version's label, for the accessible name — which states the action, per state. */
  label: string;
  iconSize: number;
  /** True when this version is the loaded one (running or paused). Skips the load window. */
  isActive: boolean;
  /** True when this version is loaded AND advancing. Chooses Pause over Play. */
  isPlaying: boolean;
  /** Cues/plays or pauses/resumes this version in the store. */
  onToggle: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | null>(null);

  // Cancel a pending window if the row unmounts mid-load — the drawer swaps panels, so this
  // button can leave the tree before the timer fires — otherwise the callback would set
  // state on a gone component.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    []
  );

  function handleClick() {
    // A second press during the window is ignored rather than queued: it would schedule a
    // second timer whose fire toggles playback straight back off.
    if (loading) return;
    // Already the loaded version: pause or resume at once, no window. Resuming is instant
    // and the position is already in the store.
    if (isActive) {
      onToggle();
      return;
    }
    // Cold start: show the spinner for the load window, then begin playback.
    setLoading(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setLoading(false);
      onToggle();
    }, LOAD_WINDOW_MS);
  }

  const stateLabel = loading ? `Loading ${label}` : isPlaying ? `Pause ${label}` : `Play ${label}`;

  return (
    <IconButton
      label={stateLabel}
      onClick={handleClick}
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: c.surface3,
        flexShrink: 0,
      }}
    >
      {loading ? (
        <Loader2 size={iconSize} color={c.text1} className="lyx-spin" />
      ) : isPlaying ? (
        <Pause size={iconSize} color={c.text1} />
      ) : (
        // The 1px nudge optically centres a triangle in a circle.
        <Play size={iconSize} color={c.text1} style={{ marginLeft: 1 }} />
      )}
    </IconButton>
  );
}

function VersionDateText({ version, suffix = '' }: { version: SongVersion; suffix?: string }) {
  const dateLabel = useLiveDateLabel(version.createdAt, version.date);
  return (
    <>
      {dateLabel}
      {suffix}
    </>
  );
}

export interface VersionsPanelProps {
  song: Song;
  /** Selected version ids, 0–2 entries. */
  compareIds: string[];
  onBack: () => void;
  onClose: () => void;
  /** Plays a version in the background, or controls the loaded transport version directly. */
  onToggleVersion: (versionId: string) => void;
  /** Cues a version paused in the transport AND retargets the edit tools onto it. */
  onLoadVersion: (versionId: string) => void;
  /** True when this version is the one loaded in the player (running or paused). */
  isVersionActive: (versionId: string) => boolean;
  /** True when this version is the loaded transport source AND actively advancing. */
  isVersionPlaying: (versionId: string) => boolean;
  /** True when this version is explicitly loaded as the source for the next edit. */
  isVersionLoadedForEditing: (versionId: string) => boolean;
  /** True when this version's background audition is active (running or paused). */
  isVersionAuditionActive: (versionId: string) => boolean;
  /** True when this version's background audition is actively playing. */
  isVersionAuditionPlaying: (versionId: string) => boolean;
  onToggleCompare: (versionId: string) => void;
  onRename: (versionId: string, currentLabel: string) => void;
  /** Copies the version's record. Free — see songsStore.duplicateVersion. */
  onDuplicate: (versionId: string) => void;
  onSaveToLibrary: (versionId: string) => void;
  onDelete: (versionId: string) => void;
  onOpenCompare: () => void;
}

export function VersionsPanel({
  song,
  compareIds,
  onBack,
  onClose,
  onToggleVersion,
  onLoadVersion,
  isVersionActive,
  isVersionPlaying,
  isVersionLoadedForEditing,
  isVersionAuditionActive,
  isVersionAuditionPlaying,
  onToggleCompare,
  onRename,
  onDuplicate,
  onSaveToLibrary,
  onDelete,
  onOpenCompare,
}: VersionsPanelProps) {
  const latest = latestVersion(song);

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Version History" onBack={onBack} onClose={onClose} />
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px' }}>
        Every action on &quot;{song.title}&quot; creates a version. Pick two to compare.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {song.versions.map((v) => {
          const inCompare = compareIds.includes(v.id);
          const loadedForEditing = isVersionLoadedForEditing(v.id);
          const activeInPlayer = isVersionActive(v.id);
          const playingInPlayer = isVersionPlaying(v.id);
          const auditionActive = isVersionAuditionActive(v.id);
          const auditionPlaying = isVersionAuditionPlaying(v.id);
          const highlighted = loadedForEditing || activeInPlayer;
          return (
            <div
              key={v.id}
              /* The flex is a class for the reason .lyx-release-row's and
                 .lyx-project-row's are — this row has to wrap at 390px, and an inline
                 `display: flex` cannot be given `flex-wrap` at a breakpoint. Four
                 controls (Play, the label, Compare, More) plus a user-named version
                 leave the name about 80px on a 380px screen. */
              className="lyx-version-row"
              role="button"
              tabIndex={0}
              aria-pressed={highlighted}
              aria-label={`Load ${v.label}`}
              onClick={() => onLoadVersion(v.id)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onLoadVersion(v.id);
                }
              }}
              style={{
                // Compare selection stays readable, and an explicitly loaded version gets its
                // own stronger highlight so "work on this one" is visible even when compare is off.
                border: `1px solid ${highlighted ? c.accent : inCompare ? c.accentBorder : c.border}`,
                borderRadius: 10,
                background: highlighted ? c.accentSoft : inCompare ? c.accentSoft : c.surface2,
                boxShadow: highlighted ? `inset 0 0 0 1px ${c.accentBorder}` : 'none',
                cursor: 'pointer',
              }}
            >
              <div onClick={(event) => event.stopPropagation()}>
                <VersionPlayButton
                  label={v.label}
                  iconSize={11}
                  isActive={activeInPlayer || auditionActive}
                  isPlaying={activeInPlayer ? playingInPlayer : auditionPlaying}
                  onToggle={() => onToggleVersion(v.id)}
                />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <p
                    style={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      margin: 0,
                      // `min-width: 0`, without which the three declarations below did
                      // nothing. This <p> is a flex item, and a flex item's automatic
                      // minimum size is its min-content width — for `nowrap` text that is
                      // the whole label, so it refused to shrink and the ellipsis never
                      // engaged. A version is named by the user (Rename is in the menu
                      // beside it), so the label is arbitrary text: "Remix with heavier
                      // drums and the second vocal take" pushed the Compare toggle and the
                      // More trigger out past the row's border at every width. The other
                      // three truncating titles in the workspace sit inside a block parent
                      // and get their width for free; this one is the exception.
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v.label}
                  </p>
                  {latest?.id === v.id && song.versions.length > 1 && (
                    <span
                      style={{
                        fontSize: 9.5,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: c.accent,
                        background: c.accentSoft,
                        border: `1px solid ${c.accentBorder}`,
                        borderRadius: 999,
                        padding: '1px 6px',
                        flexShrink: 0,
                      }}
                    >
                      Latest
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 10.5, color: c.text2, margin: '2px 0 0' }}>
                  <VersionDateText version={v} />
                </p>
              </div>

              {/* Compare stays visible: it is the one action this panel exists to
                  support, and it is a toggle whose state has to be readable.

                  The pair is grouped in a .lyx-row-controls, which is how every other
                  wrapping row in the app is built (a release, a project, a playlist
                  track). It buys two things: the 390px rule can move both controls to
                  the line below as one unit rather than letting Compare wrap and More
                  not, and the shell's existing coarse-pointer rule for
                  `.lyx-row-controls button` grows both to 40px on a touch screen —
                  which is the target §15 asks for, from the block that already
                  guarantees it everywhere else. */}
              <div
                className="lyx-row-controls"
                onClick={(event) => event.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <IconButton
                  label={inCompare ? `Deselect ${v.label}` : `Compare ${v.label}`}
                  pressed={inCompare}
                  onClick={() => onToggleCompare(v.id)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    flexShrink: 0,
                    background: inCompare ? c.surface3 : 'none',
                  }}
                >
                  <GitCompare size={14} color={inCompare ? c.accent : c.text2} />
                </IconButton>

                <ActionMenu
                  label={`More actions for ${v.label}`}
                  items={[
                    {
                      // The pass's new verb: cue this version in the transport AND make it
                      // the base the edit tools act on. First because it is the primary
                      // "work on this version" action and Version History is now the draft area.
                      label: 'Load',
                      icon: <FolderInput size={13} color={c.text2} />,
                      onSelect: () => onLoadVersion(v.id),
                    },
                    {
                      label: 'Rename',
                      icon: <Pencil size={13} color={c.text2} />,
                      onSelect: () => onRename(v.id, v.label),
                    },
                    {
                      // §7's missing verb. A record copy, not an audio copy, which is why it
                      // sits with the other free actions rather than behind the credit
                      // confirmation — see songsStore.duplicateVersion.
                      label: 'Duplicate',
                      icon: <Copy size={13} color={c.text2} />,
                      onSelect: () => onDuplicate(v.id),
                    },
                    {
                      label: 'Save to library',
                      icon: <Library size={13} color={c.text2} />,
                      onSelect: () => onSaveToLibrary(v.id),
                    },
                    // Deleting the only version would leave the song with none, so
                    // the option is absent rather than disabled — as before.
                    ...(song.versions.length > 1
                      ? [
                          {
                            label: 'Delete',
                            icon: <Trash2 size={13} color={c.danger} />,
                            onSelect: () => onDelete(v.id),
                            danger: true,
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* The prototype showed nothing until two were picked. One line of guidance
          after the first pick costs nothing and removes the guesswork. */}
      {compareIds.length === 1 && (
        <p style={{ fontSize: 11, color: c.text2, margin: '12px 0 0' }}>
          Pick one more version to compare.
        </p>
      )}
      {compareIds.length === 2 && (
        <Btn full onClick={onOpenCompare} style={{ marginTop: 14 }}>
          <GitCompare size={13} />
          Compare selected versions
        </Btn>
      )}

      {/* An <aria-pressed> toggle is announced by state, but the visual toggle is
          an icon; the counter tells sighted users the same thing the icon colour
          does, and it is the only place the pair is stated as a number. */}
      <span className="lyx-visually-hidden" role="status">
        {compareIds.length} of 2 versions selected for comparison
      </span>
    </div>
  );
}

export interface ComparePanelProps {
  song: Song;
  /** Exactly two ids; the caller only renders this panel when a pair is selected. */
  compareIds: string[];
  onBack: () => void;
  onClose: () => void;
  /** Plays a version, or pauses/resumes it when it is the loaded one. */
  onToggleVersion: (versionId: string) => void;
  /** True when this version is the one loaded in the player (running or paused). */
  isVersionActive: (versionId: string) => boolean;
  /** True when this version is loaded AND advancing. */
  isVersionPlaying: (versionId: string) => boolean;
  /** Loads this version for playback/editing and clears the comparison. */
  onLoad: (versionId: string, label: string) => void;
  /** Deletes the *other* version of the pair. */
  onDeleteOther: (versionId: string) => void;
  /** Clears the selection and returns to Version History. */
  onReset: () => void;
}

export function ComparePanel({
  song,
  compareIds,
  onBack,
  onClose,
  onToggleVersion,
  isVersionActive,
  isVersionPlaying,
  onLoad,
  onDeleteOther,
  onReset,
}: ComparePanelProps) {
  const vA = song.versions.find((v) => v.id === compareIds[0]);
  const vB = song.versions.find((v) => v.id === compareIds[1]);

  // Guard against stale ids. compareIds used to survive a version being deleted
  // (and a different song being opened), so find() returned undefined and
  // `key={v.id}` threw a TypeError that took down the whole drawer. The selection
  // is also cleared in openStudio/deleteVersion now; this is the last line of
  // defence rather than the only one.
  if (!vA || !vB) {
    return (
      <div className={PANEL_CLASS}>
        <DrawerHeader title="Compare Versions" onBack={onReset} onClose={onClose} />
        <p style={{ fontSize: 12.5, color: c.text2, lineHeight: 1.6, margin: 0 }}>
          One of the selected versions is no longer available. Pick two versions again to compare
          them.
        </p>
        <Btn full variant="secondary" onClick={onReset} style={{ marginTop: 14 }}>
          Back to Version History
        </Btn>
      </div>
    );
  }

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Compare Versions" onBack={onBack} onClose={onClose} />
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px' }}>
        Listen to both, then load the one you want to keep working on.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[vA, vB].map((v) => (
          <div
            key={v.id}
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '12px 14px',
              background: c.surface2,
            }}
          >
            <p style={{ fontSize: 12.5, fontWeight: 500, margin: '0 0 2px' }}>{v.label}</p>
            <p style={{ fontSize: 10.5, color: c.text2, margin: '0 0 10px' }}>
              <VersionDateText version={v} suffix={` · ${v.length}`} />
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <VersionPlayButton
                label={v.label}
                iconSize={12}
                isActive={isVersionActive(v.id)}
                isPlaying={isVersionPlaying(v.id)}
                onToggle={() => onToggleVersion(v.id)}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Waveform heights={seededHeights(v.id, 14)} playing={false} fill />
              </div>
            </div>
            {/* Wraps: "Load this" and "Delete other" are ~100px each at 13px with 16px of
                horizontal padding, and this card sits inside the centre column's own
                padding — at 380px the pair wants more than the line holds and the second
                button would hang over the card's edge. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Btn variant="secondary" onClick={() => onLoad(v.id, v.label)}>
                Load this
              </Btn>
              {song.versions.length > 1 && (
                <Btn variant="ghost" onClick={() => onDeleteOther(v.id === vA.id ? vB.id : vA.id)}>
                  Delete other
                </Btn>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
